import {
  DesktopRecordingStartAttemptTrace,
  DesktopRecordingStartFailurePhase,
  DesktopRecordingStartFailureSource,
  RecallPermissionKind,
  type RecallPermissionMap,
  type RecallPermissionStatus,
  type RecallSdkErrorDiagnostics,
  type RecallSdkState,
} from "@/modules/electron/types";
import {
  AudioRecordingError,
  AudioRecordingErrorSource,
  AudioRecordingErrorStage,
} from "@/domains/errors";
import { CustomError } from "@/domains/errors/kinds/custom/CustomError";

export enum DesktopRecordingPreparationState {
  WaitingForWindowId = "waiting_for_window_id",
  WaitingForUploadToken = "waiting_for_upload_token",
  PreparedWaitingForStart = "prepared_waiting_for_start",
}

export type DesktopRecordingStartFailureMetadata = {
  failureSource?: DesktopRecordingStartFailureSource;
  failurePhase?: DesktopRecordingStartFailurePhase;
  startAttemptTrace?: DesktopRecordingStartAttemptTrace | null;
  controllerKind?: DesktopControllerKind | null;
  timeoutMs?: number | null;
  noteId?: string | null;
  meetingEventWindowId?: string | null;
  recordingStrategy?: string | null;
  preparationState?: DesktopRecordingPreparationState | null;
  windowIdResolved?: boolean | null;
  uploadTokenResolved?: boolean | null;
  recallSdkState?: RecallSdkState | null;
  recallSdkErrorMessage?: string | null;
  recallSdkErrorDiagnostics?: RecallSdkErrorDiagnostics | null;
  recallPermissions?: RecallPermissionMap | null;
  restartSucceeded?: boolean | null;
  restartError?: string | null;
};

export type DesktopRecordingRestartRequiredMetadata = DesktopRecordingStartFailureMetadata;

export const buildDesktopRecordingStartAttemptLogInfo = ({
  startAttemptTrace,
}: {
  startAttemptTrace?: DesktopRecordingStartAttemptTrace | null;
}): Record<string, string | number | null> => ({
  rootStartAttemptId: startAttemptTrace?.rootStartAttemptId ?? null,
  startAttemptId: startAttemptTrace?.startAttemptId ?? null,
  attemptNumber: startAttemptTrace?.attemptNumber ?? null,
});

export const buildRecallPermissionLogInfo = ({
  recallPermissions,
}: {
  recallPermissions?: RecallPermissionMap | null;
}): Record<string, RecallPermissionStatus | null> => ({
  recallPermissionAccessibility: recallPermissions?.[RecallPermissionKind.Accessibility] ?? null,
  recallPermissionScreenCapture: recallPermissions?.[RecallPermissionKind.ScreenCapture] ?? null,
  recallPermissionSystemAudio: recallPermissions?.[RecallPermissionKind.SystemAudio] ?? null,
  recallPermissionMicrophone: recallPermissions?.[RecallPermissionKind.Microphone] ?? null,
  recallPermissionFullDiskAccess: recallPermissions?.[RecallPermissionKind.FullDiskAccess] ?? null,
});

/**
 * Thrown when the Recall SDK was restarted after a start failure.
 * The renderer should rebuild the full recording intent (fresh windowId + uploadToken)
 * and retry once at the store boundary.
 */
export class DesktopRecordingRestartRequiredError extends CustomError {
  readonly reason: string;
  readonly metadata: DesktopRecordingStartFailureMetadata | null;

  constructor(reason: string, metadata?: DesktopRecordingStartFailureMetadata | null) {
    super(`Desktop recording restart required: ${reason}`);
    this.reason = reason;
    this.metadata = metadata ?? null;
  }
}

/**
 * Thrown when Recall start recovery failed and the renderer should surface fallback UI
 * without attempting the SDK-restart retry path again.
 */
export class DesktopRecordingStartFatalError extends CustomError {
  readonly reason: string;
  readonly metadata: DesktopRecordingStartFailureMetadata | null;

  constructor(reason: string, metadata?: DesktopRecordingStartFailureMetadata | null) {
    super(`Desktop recording start fatal: ${reason}`);
    this.reason = reason;
    this.metadata = metadata ?? null;
  }
}

export type DesktopRecordingStartFailureError =
  | DesktopRecordingRestartRequiredError
  | DesktopRecordingStartFatalError;

export const isDesktopRecordingStartFailureError = (
  error: unknown
): error is DesktopRecordingStartFailureError =>
  error instanceof DesktopRecordingRestartRequiredError ||
  error instanceof DesktopRecordingStartFatalError;

export const getDesktopRecordingStartFailureError = ({
  error,
}: {
  error: unknown;
}): DesktopRecordingStartFailureError | null => {
  if (isDesktopRecordingStartFailureError(error)) {
    return error;
  }

  if (
    error instanceof AudioRecordingError &&
    isDesktopRecordingStartFailureError(error.originalError)
  ) {
    return error.originalError;
  }

  return null;
};

const NETWORK_ERROR_MESSAGE_FRAGMENTS = [
  "err_cert",
  "err_connection",
  "err_network",
  "err_name_not_resolved",
  "err_internet_disconnected",
  "failed to fetch",
  "networkerror",
  "load failed",
  "connectionreset",
  "firewall or network",
];

/**
 * True when the error looks like a connectivity failure (fetch network error,
 * Chromium net error, or a Recall connection failure) rather than a recorder
 * fault. Unwraps AudioRecordingError to inspect the original error.
 */
export const isNetworkRelatedRecordingError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }

  if (
    error instanceof AudioRecordingError &&
    error.originalError &&
    isNetworkRelatedRecordingError(error.originalError)
  ) {
    return true;
  }

  // fetch throws TypeError on network failures
  if (error.name === "TypeError") {
    return true;
  }

  // An initialization timeout that stalled waiting for the upload token is a
  // network stall: the window id resolved, so the SDK was responsive and the
  // pending credentials fetch to the backend is what never completed.
  // WaitingForWindowId stays unclassified — it is ambiguous with a wedged SDK.
  const failureMetadata = getDesktopRecordingStartFailureMetadata({ error });
  if (
    failureMetadata?.failureSource ===
      DesktopRecordingStartFailureSource.RendererInitializationTimeout &&
    failureMetadata.preparationState === DesktopRecordingPreparationState.WaitingForUploadToken
  ) {
    return true;
  }

  const message = error.message.toLowerCase();
  return NETWORK_ERROR_MESSAGE_FRAGMENTS.some(fragment => message.includes(fragment));
};

export const getDesktopRecordingStartFailureMetadata = ({
  error,
}: {
  error: unknown;
}): DesktopRecordingStartFailureMetadata | null =>
  getDesktopRecordingStartFailureError({ error })?.metadata ?? null;

export const buildDesktopRecordingStartFailureLogInfo = ({
  error,
}: {
  error: DesktopRecordingStartFailureError;
}): Record<string, unknown> => {
  const metadata = error.metadata;

  return {
    reason: error.reason,
    failureSource: metadata?.failureSource ?? null,
    failurePhase: metadata?.failurePhase ?? null,
    controllerKind: metadata?.controllerKind ?? null,
    timeoutMs: metadata?.timeoutMs ?? null,
    noteId: metadata?.noteId ?? null,
    meetingEventWindowId: metadata?.meetingEventWindowId ?? null,
    recordingStrategy: metadata?.recordingStrategy ?? null,
    preparationState: metadata?.preparationState ?? null,
    windowIdResolved: metadata?.windowIdResolved ?? null,
    uploadTokenResolved: metadata?.uploadTokenResolved ?? null,
    recallSdkState: metadata?.recallSdkState ?? null,
    recallSdkErrorMessage: metadata?.recallSdkErrorMessage ?? null,
    ...(metadata?.recallSdkErrorDiagnostics ?? {}),
    restartSucceeded: metadata?.restartSucceeded ?? null,
    restartError: metadata?.restartError ?? null,
    ...buildDesktopRecordingStartAttemptLogInfo({
      startAttemptTrace: metadata?.startAttemptTrace ?? null,
    }),
    ...buildRecallPermissionLogInfo({
      recallPermissions: metadata?.recallPermissions ?? null,
    }),
  };
};

export const buildDesktopRecordingRestartRequiredLogInfo = ({
  error,
}: {
  error: DesktopRecordingRestartRequiredError;
}): Record<string, unknown> =>
  buildDesktopRecordingStartFailureLogInfo({
    error,
  });

export type DesktopControllerKind =
  | "desktop_media_controller"
  | "desktop_artifact_media_controller";

export type DesktopControllerOperation = "initialize" | "start" | "initialize_and_start";

export const normalizeDesktopControllerError = ({
  controllerKind,
  details,
  error,
  operation,
  stage,
}: {
  controllerKind: DesktopControllerKind;
  details?: Record<string, string | number | boolean | null | undefined>;
  error: unknown;
  operation: DesktopControllerOperation;
  stage: AudioRecordingErrorStage;
}): AudioRecordingError | never => {
  // Let restart-required errors propagate without normalization —
  // the store handles them with a whole-intent rebuild.
  if (error instanceof DesktopRecordingRestartRequiredError) {
    throw error;
  }

  if (error instanceof AudioRecordingError) {
    return error;
  }

  return new AudioRecordingError({
    source: AudioRecordingErrorSource.RecallDesktop,
    stage,
    message: error instanceof Error ? error.message : String(error),
    originalError: error instanceof Error ? error : undefined,
    details: {
      controller_kind: controllerKind,
      controller_operation: operation,
      ...(details ?? {}),
    },
  });
};
